import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Skeleton } from "@redotech/redo-web/components/ui/skeleton";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

export interface InventoryModalRow {
  key: string;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  locationName?: string | null;
  onHand: number | null;
  committed: number | null;
  available: number | null;
}

interface InventoryModalProps {
  rows: InventoryModalRow[];
  isOpen: boolean;
  loading?: boolean;
  onClose: () => void;
}

const NUMBER_COLUMN_WIDTH = 108;

const NUMBER_COLUMNS: {
  key: string;
  label: string;
  value: (row: InventoryModalRow) => number | null;
}[] = [
  { key: "onHand", label: "On hand", value: (row) => row.onHand },
  { key: "committed", label: "Committed", value: (row) => row.committed },
  { key: "available", label: "Available", value: (row) => row.available },
];

type RenderItem =
  | { type: "location"; locationName: string }
  | { type: "product"; row: InventoryModalRow };

export const InventoryModal = memo(function InventoryModal({
  rows,
  isOpen,
  loading = false,
  onClose,
}: InventoryModalProps) {
  const items = useMemo<RenderItem[]>(() => {
    const groups: { locationName: string | null; rows: InventoryModalRow[] }[] =
      [];
    const groupIndex = new Map<string, number>();
    for (const row of rows) {
      const locationName = row.locationName ?? null;
      const groupKey = locationName ?? "\u0000";
      let index = groupIndex.get(groupKey);
      if (index === undefined) {
        index = groups.length;
        groupIndex.set(groupKey, index);
        groups.push({ locationName, rows: [] });
      }
      groups[index]!.rows.push(row);
    }

    const showLocationHeaders = groups.some(
      (group) => group.locationName != null,
    );

    const result: RenderItem[] = [];
    for (const group of groups) {
      if (showLocationHeaders) {
        result.push({
          type: "location",
          locationName: group.locationName ?? "Not tracked",
        });
      }
      for (const row of group.rows) {
        result.push({ type: "product", row });
      }
    }
    return result;
  }, [rows]);

  return (
    <RedoBaseModal isOpen={isOpen} onModalCloseRequested={onClose}>
      <Flex dir="column" gap="none" w="full">
        <RedoModalHeader
          cancelClicked={onClose}
          hideCloseButton
          title="Current inventory"
        />
        <Flex dir="column" gap="none" w="full">
          <Flex
            bgColor="secondary"
            borderBottomWidth="1px"
            borderColor="primary"
            borderStyle="solid"
            borderTopWidth="1px"
            justify="space-between"
            pl="2xl"
            pr="xl"
            py="lg"
            w="full"
          >
            <Text fontSize="xxs" textColor="secondary">
              Products
            </Text>
            <Flex>
              {NUMBER_COLUMNS.map((column) => (
                <Flex
                  justify="center"
                  key={column.key}
                  style={{ width: NUMBER_COLUMN_WIDTH }}
                >
                  <Text fontSize="xxs" textColor="secondary">
                    {column.label}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
          <Flex
            dir="column"
            gap="none"
            style={{ maxHeight: "50vh", overflowY: "auto" }}
            w="full"
          >
            {items.map((item, index) => {
              const notLast = index !== items.length - 1;
              if (item.type === "location") {
                return (
                  <Flex
                    align="center"
                    bgColor="secondary"
                    borderBottomWidth={notLast ? "1px" : "none"}
                    borderColor="secondary"
                    borderStyle="solid"
                    key={`location-${item.locationName}`}
                    pl="2xl"
                    pr="xl"
                    py="xs"
                    w="full"
                  >
                    <Text fontSize="xxs" textColor="secondary">
                      {item.locationName}
                    </Text>
                  </Flex>
                );
              }
              const { row } = item;
              return (
                <Flex
                  align="center"
                  borderBottomWidth={notLast ? "1px" : "none"}
                  borderColor="secondary"
                  borderStyle="solid"
                  justify="space-between"
                  key={row.key}
                  pl="2xl"
                  pr="xl"
                  py="md"
                  w="full"
                >
                  <Flex align="center" gap="md">
                    {row.imageUrl ? (
                      <img
                        alt={row.title}
                        src={row.imageUrl}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "var(--border-radius-full)",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <Flex dir="column" gap="xs" justify="center">
                      <Text fontSize="xs" fontWeight="medium">
                        {row.title}
                      </Text>
                      {row.subtitle ? (
                        <Text fontSize="xxs" textColor="tertiary">
                          {row.subtitle}
                        </Text>
                      ) : null}
                    </Flex>
                  </Flex>
                  <Flex align="center">
                    {NUMBER_COLUMNS.map((column) => (
                      <Flex
                        justify="center"
                        key={column.key}
                        style={{ width: NUMBER_COLUMN_WIDTH }}
                      >
                        {loading ? (
                          <Skeleton height={12} width={28} />
                        ) : (
                          <Text
                            fontSize="xs"
                            fontWeight="thin"
                            textColor="secondary"
                          >
                            {column.value(row) ?? "N/A"}
                          </Text>
                        )}
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
        <Flex
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="row"
          gap="md"
          justify="flex-end"
          px="2xl"
          py="lg"
          w="full"
        >
          <RedoButton
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Close"
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
